import { Card, Heatmap, KpiTile, RowMeter, StackedBars, formatNumber, formatPercent } from "@omnabase/ui";
import { change, formatMinutes, type OverviewRead } from "@omnabase/shared";
import { useFetch } from "../../lib/inbox";
import { useNav, useWs } from "../../lib/nav";
import { NothingYet, ReportLoading, versus, type ReportContext } from "./ReportsScreen";

/** The Overview (brief In scope 1): the four tiles, the bars per day or week, what people write about, when tickets arrive, and the
 *  by-project table when the scope holds several projects. Everything is the API's arithmetic; here it is formatted and drawn. */
const minutes = (min: number | null) => (min === null ? { value: "—", unit: "" } : formatMinutes(min));
const both = (min: number | null) => (min === null ? "—" : Object.values(formatMinutes(min)).join(" "));

export function ReportOverview({ ctx }: { ctx: ReportContext }) {
  const nav = useNav(); const { ws } = useWs();
  const r = useFetch<OverviewRead>(`/api/v1/w/${ctx.slug}/reports?${ctx.query}`, [ctx.query]);
  if (!r.data) return r.error ? <p className="ob-report__note">{r.error}</p> : <ReportLoading />;
  const d = r.data; const c = d.current, p = d.previous;
  if (c.received === 0 && p.received === 0) return <NothingYet ctx={ctx} />;
  const reply = minutes(c.firstReplyMin);
  const weekly = ctx.range.days > 31;
  const top = d.topics[0]?.count ?? 1;
  return (
    <>
      <div className="ob-kpis">
        <KpiTile value={formatNumber(c.received)} unit="tickets" label="Received" sub={versus(change(c.received, p.received), ctx.range.days)} />
        <KpiTile value={c.received ? formatPercent(c.resolved / c.received) : "—"} label="Resolved" sub={`${formatNumber(c.resolved)} of ${formatNumber(c.received)}`} />
        <KpiTile value={reply.value} unit={reply.unit} label="First reply · median" sub={`People alone: ${both(c.humanReplyMin)}`} />
        <KpiTile value={formatPercent(c.aiShare)} label="Answered by the AI alone" sub={versus(change(c.aiShare, p.aiShare, "points"), ctx.range.days)} />
      </div>
      <Card title={weekly ? "Tickets per week" : "Tickets per day"}>
        <StackedBars data={d.buckets} legend={{ people: "Handled by people", ai: "Answered by the AI alone" }} />
        {weekly && d.buckets[0] && d.buckets[0].days < 7 && <p className="ob-report__note">The first bar holds {d.buckets[0].days === 6 ? "six" : String(d.buckets[0].days)} days; every other bar a full week, the newest ending today.</p>}
      </Card>
      <div className="ob-grid-2 ob-grid-2--even">
        <Card title="What people write about">
          {d.topics.length === 0 ? <p className="ob-report__note">No tags on the period's tickets yet.</p> : (
            <ul className="ob-topics">
              {d.topics.map((t) => {
                const body = <><span className="ob-topics__name">{t.name ?? "deleted tag"}</span><RowMeter value={t.count / top} /><span className="ob-topics__count">{formatNumber(t.count)}</span><span className="ob-topics__pct">{formatPercent(t.share)}</span></>;
                if (!t.name) return <li key={t.tagId}><span className="ob-topics__row ob-topics__row--gone">{body}</span></li>;
                const to = ws(`/inbox/all-open?tag=${encodeURIComponent(t.tagId)}${ctx.project ? `&project=${ctx.project.id}` : ""}`);
                return <li key={t.tagId}><a className="ob-topics__row" href={to} onClick={(e) => nav(to, e)}>{body}</a></li>;
              })}
            </ul>
          )}
        </Card>
        <Card title="When tickets arrive · a typical week"><Heatmap days={d.heatmap.days} rows={d.heatmap.rows} /></Card>
      </div>
      {d.byProject && (
        <Card title="By project" className="ob-card--table">
          <div className="ob-dtable-card">
            <table className="ob-dtable">
              <thead><tr><th>Project</th><th className="ob-dtable__num">Tickets</th><th className="ob-dtable__num ob-dtable__col--phone">Resolved</th><th className="ob-dtable__num">First reply</th><th className="ob-dtable__num">By the AI alone</th></tr></thead>
              <tbody>
                {d.byProject.map((pr) => {
                  const to = ctx.link("/reports", pr.projectId);
                  return (
                    <tr key={pr.projectId}>
                      <td className="ob-dtable__grow"><a className="ob-dtable__name" href={to} onClick={(e) => nav(to, e)} title={pr.name}>{pr.name}</a></td>
                      <td className="ob-dtable__num">{formatNumber(pr.received)}</td>
                      <td className="ob-dtable__num ob-dtable__col--phone">{pr.received ? formatPercent(pr.resolved / pr.received) : "—"}</td>
                      <td className="ob-dtable__num">{pr.received ? both(pr.firstReplyMin) : "—"}</td>
                      <td className="ob-dtable__num">{pr.received ? formatPercent(pr.aiShare) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <p className="ob-report__note">Today is counted live; the days before it are stored each night and settle after three days, when late replies and reopened tickets have landed.</p>
    </>
  );
}
